import { useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import {
  usersTicketsPageQuery,
  usersTicketsQueryString,
  usersTicketsSummaryQuery,
  usersTicketsUnassignedQuery,
  usersTicketsUserQuery,
  type UsersTicketsBar,
  type UsersTicketsFilters,
  type UsersTicketsRow,
  type UsersTicketsScope,
} from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { NotInstalled } from './WallHeader'

/**
 * "Users Tickets Dashboard" - `easeedesk-dashboards/Users_tickets`, macheta
 * `views/version-3/dashboards/easeedesk/users_tickets/index.php` si
 * `public/js/dashboards/users_tickets.js`.
 *
 * Un rand pe om, cu cate are active, cate escaladate, cate a rezolvat, de cand
 * sta cel mai vechi si cat ii ia in medie; randul se apasa si isi arata
 * tichetele. Langa tabel, tichetele pe care nu le are nimeni, cele mai vechi
 * deschise, si cele doua siruri de bare - pe tip si pe stare - desenate ca in
 * macheta, cu `<table class="utd-bars">`, nu cu o biblioteca de grafice.
 *
 * DURATELE se scriu ca `humanDuration()` din controllerul vechi, ca ecranul si
 * foaia exportata sa se citeasca la fel.
 *
 * CE NU S-A MUTAT, SI SE VEDE: filele (`#utd-tabs`), cautarea dupa nume,
 * paginarea si butonul de ecran plin. Sunt inlesniri ale ecranului vechi, nu
 * cifre; tabelul, filtrele si cele doua exporturi sunt aici toate. Vezi
 * descrierea PR-ului.
 */

const STYLE = `
.utd-root { --surface-1: #fff; --text-muted: #898781; --series-active: #2a78d6; --series-resolved: #eb6834; }
.utd-root .utd-card { background: var(--surface-1); border: 1px solid #ddd; }
.utd-root #users-tickets-table { background: #fff; border: 0; margin-bottom: 0; }
.utd-root #users-tickets-table > thead > tr > th { border-bottom: 2px solid #ddd; white-space: nowrap; text-align: left; }
.utd-root #users-tickets-table > thead > tr > th.text-center { text-align: center; }
.utd-root tr.user-row { cursor: pointer; }
.utd-root tr.user-row:hover { background-color: #f5f5f5; }
.utd-root tr.user-row.open { background-color: #eef5fb; }
.utd-root tr.tickets-row > td { padding: 0; background-color: #fafafa; }
.utd-root .group-flag { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.utd-root .utd-bars { width: 100%; }
.utd-root .utd-bars td { padding: 2px 4px; font-size: 12px; vertical-align: middle; }
.utd-root .utd-bar { height: 12px; display: block; border-radius: 2px; }
.utd-root .utd-tile { border: 1px solid #ddd; padding: 10px; text-align: center; margin-bottom: 10px; }
.utd-root .utd-tile .number { font-size: 28px; font-weight: bold; }
.utd-root .utd-tile .desc { color: var(--text-muted); font-size: 12px; text-transform: uppercase; }
`

/** `humanDuration()` din controller, cuvant cu cuvant. */
export function humanDuration(seconds: number): string {
  if (seconds <= 0) return '-'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  const years = Math.floor(days / 365.25)
  if (years >= 1) {
    const monthsLeft = Math.floor((days - years * 365.25) / 30.44)
    return `${years}y${monthsLeft > 0 ? ` ${monthsLeft}mo` : ''}`
  }

  const months = Math.floor(days / 30.44)
  if (months >= 1) {
    const daysLeft = Math.floor(days - months * 30.44)
    return `${months}mo${daysLeft > 0 ? ` ${daysLeft}d` : ''}`
  }

  if (days > 0) return `${days}d ${hours}h`

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

function Bars({ id, rows }: { id: string; rows: UsersTicketsBar[] }) {
  const top = Math.max(1, ...rows.map((r) => r.total))

  return (
    <table className="utd-bars" id={id}>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name}>
            <td style={{ width: '35%' }}>{row.name}</td>
            <td>
              <span
                className="utd-bar"
                style={{ width: `${Math.round((row.total / top) * 100)}%`, backgroundColor: row.color || '#2a78d6' }}
              />
            </td>
            <td style={{ width: 40, textAlign: 'right' }}>{row.total}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Picker({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { id: number; name: string }[]
  value: number[]
  onChange(next: number[]): void
}) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <select
        multiple
        className="form-control"
        aria-label={label}
        value={value.map(String)}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => Number(o.value)))}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  )
}

function UserTickets({ userId, filters }: { userId: number; filters: UsersTicketsFilters }) {
  const q = useQuery(usersTicketsUserQuery(userId, filters))

  return (
    <table className="table table-condensed tickets-table">
      <tbody>
        {(q.data?.tickets ?? []).map((ticket) => (
          <tr key={ticket.id}>
            <td style={{ width: 70 }}>{ticket.id}</td>
            <td style={{ width: 70 }}>
              <span className="label" style={{ backgroundColor: ticket.color || '#777' }}>
                {ticket.short_name}
              </span>
            </td>
            <td>{ticket.subject}</td>
            <td style={{ width: 160 }}>{ticket.company_name}</td>
            <td style={{ width: 120 }}>{ticket.status_name}</td>
            <td style={{ width: 100, textAlign: 'right' }}>{humanDuration(ticket.age)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function UsersTicketsPage() {
  const [filters, setFilters] = useState<UsersTicketsFilters>({
    scope: 'all',
    from: '',
    to: '',
    groups: [],
    types: [],
    statuses: [],
  })
  const [open, setOpen] = useState<number | null>(null)

  const page = useQuery(usersTicketsPageQuery).data
  const summary = useQuery(usersTicketsSummaryQuery(filters)).data
  const unassigned = useQuery(usersTicketsUnassignedQuery(filters)).data
  const users: UsersTicketsRow[] = summary?.users ?? []
  const resolved = users.reduce((total, row) => total + row.resolved, 0)
  const query = usersTicketsQueryString(filters)

  return (
    <Content>
      <style>{STYLE}</style>
      <div id="content" className="utd-root">
        <div className="row">
          <div className="col-md-9">
            <h4>{page?.title ?? 'Easeedesk :: Users Tickets Dashboard'}</h4>
          </div>
          <div className="col-md-3 text-right">
            <a className="btn btn-default btn-sm" id="utd-export" href={`/api/v2/easeedesk/dashboards/users-tickets/export?${query}`}>
              <i className="far fa-file-excel" aria-hidden="true" /> Export
            </a>
          </div>
        </div>

        {page && !page.ready ? <NotInstalled what="Users Tickets" /> : null}

        <div className="row">
          <div className="col-md-3">
            <form id="dashboard-filters" onSubmit={(e) => e.preventDefault()}>
              <div className="form-group">
                <label>Scope</label>
                <select
                  className="form-control"
                  aria-label="Scope"
                  value={filters.scope}
                  onChange={(e) => setFilters({ ...filters, scope: e.target.value as UsersTicketsScope })}
                >
                  <option value="all">All</option>
                  <option value="active">Active only</option>
                  <option value="resolved">Resolved only</option>
                </select>
              </div>
              <div className="form-group">
                <label id="utd-label-from">Resolved from</label>
                <input
                  type="date"
                  className="form-control"
                  aria-label="Resolved from"
                  value={filters.from}
                  onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label id="utd-label-to">Resolved to</label>
                <input
                  type="date"
                  className="form-control"
                  aria-label="Resolved to"
                  value={filters.to}
                  onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                />
              </div>
              <Picker label="Groups" options={page?.groups ?? []} value={filters.groups} onChange={(groups) => setFilters({ ...filters, groups })} />
              <Picker label="Types" options={page?.types ?? []} value={filters.types} onChange={(types) => setFilters({ ...filters, types })} />
              <Picker label="Statuses" options={page?.statuses ?? []} value={filters.statuses} onChange={(statuses) => setFilters({ ...filters, statuses })} />
            </form>
          </div>

          <div className="col-md-9">
            <div className="row" id="utd-tiles">
              <div className="col-md-3">
                <div className="utd-tile">
                  <div className="number" style={{ color: 'var(--series-active)' }}>
                    {users.reduce((total, row) => total + row.active, 0)}
                  </div>
                  <div className="desc">Active</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="utd-tile">
                  <div className="number" style={{ color: 'var(--series-resolved)' }}>
                    {resolved}
                  </div>
                  <div className="desc">Resolved</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="utd-tile">
                  <div className="number">{summary?.unassigned ?? 0}</div>
                  <div className="desc">Unassigned</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="utd-tile">
                  <div className="number">{summary?.previous_resolved ?? 0}</div>
                  <div className="desc">Previous period</div>
                </div>
              </div>
            </div>

            <div className="utd-card">
              <table className="table table-condensed" id="users-tickets-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Group</th>
                    <th className="text-center">Active</th>
                    <th className="text-center">Escalated</th>
                    <th className="text-center">Over SLA</th>
                    <th className="text-center">Resolved</th>
                    <th className="text-center">Oldest active</th>
                    <th className="text-center">Avg. resolve</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((row) => (
                    <Fragment key={row.user_id}>
                      <tr

                        id={`user_${row.user_id}`}
                        className={open === row.user_id ? 'user-row open' : 'user-row'}
                        onClick={() => setOpen(open === row.user_id ? null : row.user_id)}
                      >
                        <td>
                          <span
                            className="group-flag"
                            style={{ backgroundColor: row.online ? '#0ca30c' : '#c3c2b7' }}
                            title={row.online ? 'Online' : 'Offline'}
                          />
                          {row.username}
                        </td>
                        <td>
                          <span className="group-flag" style={{ backgroundColor: row.group_color }} />
                          {row.group_name}
                        </td>
                        <td className="text-center">{row.active}</td>
                        <td className="text-center">{row.escalated}</td>
                        <td className="text-center">{row.over_sla}</td>
                        <td className="text-center">{row.resolved}</td>
                        <td className="text-center">{humanDuration(row.oldest_active)}</td>
                        <td className="text-center">{humanDuration(row.avg_resolve)}</td>
                      </tr>
                      {open === row.user_id ? (
                        <tr key={`${row.user_id}-tickets`} className="tickets-row">
                          <td colSpan={8}>
                            <div className="utd-tickets-bar">
                              <span>{row.username}</span>
                              <a href={`/api/v2/easeedesk/dashboards/users-tickets/user/${row.user_id}/export?${query}`}>
                                Export
                              </a>
                            </div>
                            <UserTickets userId={row.user_id} filters={filters} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ marginTop: 15 }}>
              <div className="col-md-6">
                <h5>Tickets by type</h5>
                <Bars id="utd-type-chart" rows={summary?.by_type ?? []} />
              </div>
              <div className="col-md-6">
                <h5>Tickets by status</h5>
                <Bars id="utd-status-chart" rows={summary?.by_status ?? []} />
              </div>
            </div>

            <div className="row" style={{ marginTop: 15 }}>
              <div className="col-md-6">
                <h5>Oldest open</h5>
                <div id="utd-oldest-list">
                  {(summary?.oldest_open ?? []).map((ticket) => (
                    <div key={ticket.id} style={{ padding: '4px 0' }}>
                      <span className="label" style={{ backgroundColor: ticket.color || '#777', marginRight: 6 }}>
                        {ticket.short_name}
                      </span>
                      {ticket.subject} <small>{humanDuration(ticket.age)}</small>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-6">
                <h5>Unassigned</h5>
                <div id="utd-unassigned-list">
                  {(unassigned?.tickets ?? []).map((ticket) => (
                    <div key={ticket.id} className="utd-unassigned-row" style={{ padding: '4px 0' }}>
                      {ticket.id} {ticket.subject} <small>{humanDuration(ticket.age)}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
